import type { ReactNode } from 'react';
import type { IPermission } from '@condo/domain';
import { Loader2 } from 'lucide-react';
import { NoAccessPage } from './NoAccessPage';
import { RequirePermissions } from './RequirePermissions';

export interface RequirePagePermissionsProps {
    permissions: IPermission[];
    children: ReactNode;
    /**
     * Override the denied state UI. Defaults to `<NoAccessPage />` so the
     * user always sees an explanation rather than an empty screen.
     */
    fallback?: ReactNode;
}

function PagePermissionLoadingState() {
    return (
        <div className="flex min-h-[60vh] w-full items-center justify-center p-6" role="status" aria-live="polite" aria-label="Checking page access">
            <Loader2 className="h-6 w-6 animate-spin text-primary motion-reduce:animate-none" aria-hidden />
        </div>
    );
}

/**
 * Page-level permission gate. Same ALL-of semantics as
 * `RequirePermissions`, but defaults the denied state to the full
 * `NoAccessPage` rather than rendering nothing.
 */
export function RequirePagePermissions({ permissions, children, fallback = <NoAccessPage /> }: RequirePagePermissionsProps) {
    return (
        <RequirePermissions permissions={permissions} loadingFallback={<PagePermissionLoadingState />} fallback={fallback}>
            {children}
        </RequirePermissions>
    );
}
